"use client";

import { Button } from "@/components/atoms/button";
import { Icon } from "@/components/atoms/icon";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/atoms/popover";

import {
  getPathname,
  Locale,
  LOCALE_NAMES,
  LOCALE_SELECT_PLACEHOLDER,
  routing,
  usePathname,
} from "@/i18n/routing";
import { cn } from "@/lib/utils";
import { Check, Globe } from "lucide-react";
import { useLocale } from "next-intl";
import { useCallback } from "react";
import Cookies from "js-cookie";

export default function LocaleSwitcher({
  design,
  isScrolled: _isScrolled = false,
}: {
  design?: string;
  isScrolled?: boolean;
}) {
  const pathname = usePathname();
  const locale = useLocale();
  const onChangeLanguage = useCallback(
    (nextLocale: Locale) => {
      const url = getPathname({
        href: { pathname },
        locale: nextLocale,
      });
      Cookies.set('locale', nextLocale);
      globalThis.location.assign(url); // safer alternative to href=
    },
    [pathname],
  );

  const isCRIDesign = "cri" === design;
  const isArtDesign = "art" === design;
  const isMspDesign = "msp" === design;
  return (
    <div className={`relative lg:flex ${isCRIDesign ? "flex w-full font-[700]" : isArtDesign || isMspDesign ? "flex" : "hidden"}`}>
      <Popover>
      <PopoverTrigger asChild>
          <Button
            hierarchy="ghost"
            size="lg"
            className={cn(
              // Shared header styling — ART/MSP use this too so their button matches
              // the WanWan and main headers.
              // NB: font-[700] not font-bold — globals.css maps --font-* to weights,
              // which Tailwind v4 reads as the font-FAMILY namespace, so the named
              // weight utilities emit invalid CSS and are dropped.
              "flex h-auto min-h-9 items-center justify-between gap-1 whitespace-normal rounded-lg border border-black px-1 py-2 text-sm text-gray-700 hover:text-gray-900",
              // ART header bar is white; give the button a solid white fill too and
              // pin hover so the ghost variant cannot tint it.
              isArtDesign && "bg-white hover:bg-white active:bg-white",
              // MSP header sits on a near-black bar: white fill, black text.
              isMspDesign &&
                " bg-white text-black hover:bg-white hover:text-black active:bg-white",
              // CRI opts out: no border, full width, its own padding, one line.
              isCRIDesign &&
                "border-0 bg-white rounded-none w-full min-w-0 gap-2 px-4 whitespace-nowrap text-sm font-[700] text-[#1F1F1F]",
            )}
          >
            <div className={cn("flex items-center", isCRIDesign ? "gap-2" : "gap-1")}>
              {!isCRIDesign && <Globe className="h-5 w-5 shrink-0" />}
              {/* max-w-min caps the label at its widest word ("Language"), so it
                  always breaks at the space onto two lines. Font-independent —
                  no pixel guess. CRI keeps one line via its whitespace-nowrap. */}
              <span
                data-wovn-ignore
                className={cn(
                  "min-w-0 text-center leading-tight",
                  !isCRIDesign && "max-w-min leading-none",
                )}
              >
                {LOCALE_SELECT_PLACEHOLDER}
              </span>
            </div>
            <Icon icon="arrow-down" className="shrink-0" />
          </Button>
        </PopoverTrigger>

        {/* Radix measures the trigger and exposes its width to the content, so the
            dropdown always matches the button exactly. */}
        <PopoverContent
          align="start"
          className={`mt-2 w-[var(--radix-popover-trigger-width)] rounded-lg border border-[var(--fill-gray-light)] bg-white py-2 shadow-lg ${isCRIDesign ? "border-none rounded-none mt-1 font-[700]" : ""}`}
        >
          {routing.locales.map((lang) => (
            <Button
              data-wovn-ignore
              hierarchy={"ghost"}
              key={lang}
              aria-current={locale === lang ? "true" : undefined}
              className={cn(
                "w-full justify-between text-left",
                isCRIDesign && "border-none bg-white rounded-none w-full font-[700] text-[#1F1F1F]",
                // Selected row reuses the ghost pressed fill, a shade darker than hover
                locale === lang &&
                  "bg-[var(--fill-primary-tonal-normal)] text-[var(--text-base-primary)]",
              )}
              onClick={() => onChangeLanguage(lang)}
            >
              <span>{LOCALE_NAMES[lang]}</span>
              {locale === lang && <Check className="h-4 w-4 shrink-0" />}
            </Button>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
}
